import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { QueueCustomCalculationsArgs } from "@/shared/Executions/models/QueueCustomCalculationsArgs";
import { QueueCustomReportsArgs } from "@/shared/Executions/models/QueueCustomReportsArgs";
import { QueueDataCleaningArgs } from "@/shared/Executions/models/QueueDataCleaningArgs";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { QueueNCAAnalysisArgs } from "@/shared/Executions/models/QueueNCAAnalysisArgs";
import { QueuePlotsArgs } from "@/shared/Executions/models/QueuePlotsArgs";
import { QueueTablesArgs } from "@/shared/Executions/models/QueueTablesArgs";

// builder type keyed by a property of QueueExecutionArgs
type QueueExecutionRequestBuilder<K extends keyof QueueExecutionArgs> =
    (value: QueueExecutionArgs[K], payload: IQueueExecutionRequest, args: QueueExecutionArgs) => void;

export class QueueExecutionRequestFactory {

    public static readonly requestBuilders = new Map<string, QueueExecutionRequestBuilder<any>>();

    private static register<K extends keyof QueueExecutionArgs>(key: K, builder: QueueExecutionRequestBuilder<K>) {
        this.requestBuilders.set(key as string, builder as QueueExecutionRequestBuilder<any>);
    }

    public static initialize(): void {
        if (this.requestBuilders.size > 0) return; // idempotent / lazy-safe
        QueueExecutionRequestFactory.register("dataCleaningArgs", (value: QueueDataCleaningArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            payload.fileId = value.fileId;
            const dataCleaning: any = {
                rules: value.rules
            };
            if (value?.outputFileName) {
                dataCleaning.output = { fileName: value.outputFileName };
            }
            payload.dataCleaning = dataCleaning;
        });

        //@ts-ignore
        QueueExecutionRequestFactory.register("ncaAnalysisArgs", (value: QueueNCAAnalysisArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            payload.fileId = value.fileId;
            payload.name = value.name;
            payload.calculation = value.analysisConfiguration;
        });

        QueueExecutionRequestFactory.register("customCalculationsArgs", (value: QueueCustomCalculationsArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            if (value.parentExecutionId) payload.parentExecutionId = value.parentExecutionId;
            payload.name = value.name;

            const customCalculations: any = {
                calculations: value.calculations,
                output: {
                    sort: value.sortColumns
                }
            };
            payload.customCalculations = customCalculations;
        });

        QueueExecutionRequestFactory.register("plotArgs", (value: QueuePlotsArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            if (value.parentExecutionId) payload.parentExecutionId = value.parentExecutionId;
            payload.name = value.name;

            const customPlots: any = {
                plots: value.plots,
            };

            payload.customPlots = customPlots;
        });

        QueueExecutionRequestFactory.register("tableArgs", (value: QueueTablesArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            if (value.parentExecutionId) payload.parentExecutionId = value.parentExecutionId;
            payload.name = value.name;

            const customTables: any = {
                tables: value.tables,
            };

            payload.customTables = customTables;
        });

        QueueExecutionRequestFactory.register("customReportArgs", (value: QueueCustomReportsArgs | undefined, payload: IQueueExecutionRequest) => {
            if (!value) return;
            if (value.parentExecutionId) payload.parentExecutionId = value.parentExecutionId;
            payload.name = value.name;

            const customReports: any = {
                reports: value.customReports,
            };

            if (value.templateSelection) {
                customReports.template_id = value.templateSelection.template_id;
                customReports.template_version = value.templateSelection.version;
            }

            payload.customReports = customReports;
        });




        //TODO: other payload builders
        /*
         "meta_data": { // prescribed keys?  }
        */
    }

    public static createQueueExecutionRequest(args: QueueExecutionArgs): IQueueExecutionRequest {

        // ensure builders registered lazily to avoid module init cycles
        this.initialize();

        const payload = {} as IQueueExecutionRequest;

        for (const [k, v] of Object.entries(args)) {
            if (v === undefined || v === null) continue;
            const h = QueueExecutionRequestFactory.requestBuilders.get(k);
            if (h) {
                h(v as any, payload, args);
            } else {
                QueueExecutionRequestFactory.defaultRequestBuilder(k, v, payload);
            }
        }

        // advanced analysis
        //TODO better way to differentiate advanced analysis vs. generic just have props on the args model
        if (args.name != null) payload.name = args.name;
        if (args.anyalsisFileId != null) payload.fileId = args.anyalsisFileId;

        return payload;
    }

    //@ts-ignore
    public static defaultRequestBuilder(key: string, value: any, payload: IQueueExecutionRequest) {
        //(payload as any)[key] = value;
    }


}
